import { DateTime } from 'luxon';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { mixed, number, object } from 'yup';

import { useToast } from 'Component/Alerta';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, { Arquivo, Botao, Selecao } from 'Component/Form';
import LayoutDefault from 'Component/Layout';
import RelogioDePonto from 'Interface/RelogiosDePonto';
import { ANOS, MESES, PDF } from 'View/constants';
import { useAdicionarRelogioDePontoMutation } from 'View/Service/Default/RelogiosDePonto';
import { useRecuperarVinculosEmpregaticiosQuery } from 'View/Service/Default/VinculosEmpregaticios';

const regras = object({
  vinculo_id: number().required('Obrigatório'),
  mes: number().required('Obrigatório'),
  ano: number().required('Obrigatório'),
  url: mixed()
    .test('tamanho', 'O arquivo é muito grande', (valor) => {
      if (valor && valor.size) {
        return valor.size <= 500000;
      }
      return true;
    })
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return PDF.includes(valor.type);
      }
      return true;
    })
    .optional(),
});

const Adicionar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const { usuarioId = 0 } = useParams();
  const [adicionarRelogioDePonto] = useAdicionarRelogioDePontoMutation();

  const { data: { vinculos } = {} } = useRecuperarVinculosEmpregaticiosQuery({
    finder: 'listaJson',
    contain: ['Empresas'],
    options: { usuario_id: usuarioId },
  });

  const enviar = async (dados: RelogioDePonto): Promise<void> => {
    try {
      await adicionarRelogioDePonto(dados).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      navegacao(`/usuarios/editar/${usuarioId}`);
    } catch (error) {
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Adicionando arquivo de relogio de ponto"
        descricao="Preencha o formulário e clique em enviar para adicionar o arquivo"
      />
      <Form onSubmit={enviar} regras={regras}>
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-12">
                  <Selecao
                    name="vinculo_id"
                    rotulo="Vinculo empregatício"
                    opcoes={vinculos}
                    autoSelecao
                  />
                </div>
                <div className="col-md-6">
                  <Selecao
                    name="mes"
                    rotulo="Mês"
                    opcoes={MESES}
                    inicial={DateTime.now().month}
                  />
                </div>
                <div className="col-md-6">
                  <Selecao
                    name="ano"
                    rotulo="ano"
                    opcoes={ANOS}
                    inicial={DateTime.now().year}
                  />
                </div>
                <div className="col-md-12">
                  <Arquivo name="url" />
                </div>
              </div>

              <div className="mt-5">
                <Botao>Enviar</Botao>
                <BotaoLink
                  tema="light"
                  to={`/usuarios/editar/${usuarioId}`}
                  className="ms-2"
                >
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </LayoutDefault>
  );
};

export default Adicionar;
